import { gql, useSubscription } from "@apollo/client";
import {
  AutopilotPreview_AutopilotSessionFragmentDoc,
  AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
  AutopilotPreviewSteps_AutopilotSessionFragment,
  AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscription,
  AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscriptionVariables,
  AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponseFragmentDoc,
  AutopilotSessionPreviewResponse,
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
  InternalAutopilotSessionPreviewStep,
} from "../../__generated__/graphql";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import CuratedSearchDesignTextOnlyStep, {
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep,
} from "../autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignStep/CuratedSearchDesignTextOnlyStep";
import {
  useFragment as getFragmentData,
  makeFragmentData,
} from "../../__generated__";
import AutopilotPreview, {
  AutopilotPreview_AutopilotSessionPreview,
  AutopilotPreview_AutopilotSession,
} from "./AutopilotPreview";
import AutopilotPreviewContactStep, {
  AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep,
} from "./AutopilotPreviewContactStep";
import CiroAlert from "../shared/CiroAlert";
import { PreviewContactFeedbackMap } from "../../contexts/InternalAutopilotHydrationContext";
import CuratedSearchCardHeader from "../autopilotCuratedSearch/CuratedSearchCard/CuratedSearchCardHeader";
import CiroExpandableCard from "../shared/CiroExpandableCard";
import AutopilotPreviewSubheaderActions, {
  AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse,
} from "./AutopilotPreviewSubheaderActions";

const AutopilotPreviewSteps_InternalAutopilotSessionPreview = gql`
  subscription AutopilotPreviewSteps_InternalAutopilotSessionPreview(
    $curatedSearchId: Int!
  ) {
    internalAutopilotSessionPreview(curatedSearchId: $curatedSearchId) {
      ... on AutopilotSearchDesignTextOnlyStep {
        id
        ephemeral
        ...CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep
      }
      ... on AutopilotSessionPreviewResponseStep {
        id
        ephemeral
        autopilotSessionPreviewResponseStepData {
          ...AutopilotPreview_AutopilotSessionPreview
          ...AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse
          llmFeedback
        }
      }
      ... on AutopilotSessionPreviewContactStep {
        id
        ephemeral
        autopilotSessionPreviewContactStepData {
          ...AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep
        }
      }
    }
  }

  ${CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep}
  ${AutopilotPreview_AutopilotSessionPreview}
  ${AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep}
  ${AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponse}
`;

export const AutopilotPreviewSteps_AutopilotSession = gql`
  fragment AutopilotPreviewSteps_AutopilotSession on AutopilotSession {
    ...AutopilotPreview_AutopilotSession
  }

  ${AutopilotPreview_AutopilotSession}
`;

interface IAutopilotPreviewSteps {
  curatedSearchId: number | null;
  isRemixStep?: boolean;
  previewStep: AutopilotSessionPreviewResponse | null;
  setPreviewStep: (value: AutopilotSessionPreviewResponse | null) => void;
  previewContactFeedback: PreviewContactFeedbackMap;
  setPreviewContactFeedback: (feedback: PreviewContactFeedbackMap) => void;
  existingAutopilotSession: AutopilotPreviewSteps_AutopilotSessionFragment | null;
}

const AutopilotPreviewSteps = ({
  curatedSearchId,
  isRemixStep,
  previewStep,
  setPreviewStep,
  previewContactFeedback,
  setPreviewContactFeedback,
  existingAutopilotSession,
}: IAutopilotPreviewSteps) => {
  const [error, setError] = useState<Error | null>(null);
  const [minimized, setMinimized] = useState(false);
  const isRunning = Boolean(curatedSearchId && !previewStep);

  const { data } = useSubscription<
    AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscription,
    AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscriptionVariables
  >(AutopilotPreviewSteps_InternalAutopilotSessionPreview, {
    variables: {
      curatedSearchId: curatedSearchId || 0,
    },
    skip: !isRunning,
    onError: (error) => {
      console.error("Error in AutopilotPreviewSteps:", error);
      setError(error);
    },
  });

  const [processedSteps, setProcessedSteps] = useState<
    InternalAutopilotSessionPreviewStep[]
  >([]);

  useEffect(() => {
    if (data?.internalAutopilotSessionPreview) {
      const step =
        data.internalAutopilotSessionPreview as InternalAutopilotSessionPreviewStep;
      if (
        step.__typename === "AutopilotSearchDesignTextOnlyStep" ||
        step.__typename === "AutopilotSessionPreviewContactStep"
      ) {
        setProcessedSteps((previous) => [...previous, step]);
      } else if (step.__typename === "AutopilotSessionPreviewResponseStep") {
        setPreviewStep(step.autopilotSessionPreviewResponseStepData);
      } else {
        console.error("Unknown step type:", step.__typename);
      }
    }
  }, [data, setPreviewStep]);

  useEffect(() => {
    if (!curatedSearchId) {
      setProcessedSteps([]);
      setError(null);
    }
  }, [curatedSearchId]);

  if (!curatedSearchId) {
    return null;
  }

  return (
    <div
      className={classNames(
        "w-full",
        "py-4",
        "overflow-hidden",
        "transition-all",
      )}
    >
      <CiroExpandableCard
        isOpen={!minimized}
        setIsOpen={() => setMinimized(!minimized)}
        title={
          <CuratedSearchCardHeader
            headerText="Review sample contacts"
            headerIcon={
              <MagnifyingGlassIcon className="h-6 w-6 text-orange-500" />
            }
          />
        }
        collapsedContent={
          <div className={classNames("flex", "gap-2")}>
            <AutopilotPreviewSubheaderActions
              autopilotSessionPreview={
                getFragmentData(
                  AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponseFragmentDoc,
                  previewStep
                    ? makeFragmentData(
                        previewStep,
                        AutopilotPreviewSubheaderActions_AutopilotSessionPreviewResponseFragmentDoc,
                      )
                    : null,
                ) ?? null
              }
              curatedSearchId={curatedSearchId}
            />
          </div>
        }
      >
        {error && (
          <CiroAlert
            message={`Error loading preview. Please try again or contact support@ciro.io.`}
            level="error"
          />
        )}

        <div>
          <div className={classNames("flex", "flex-col", "gap-2")}>
            {processedSteps.map((step, idx) => {
              if (step.ephemeral && idx !== processedSteps.length - 1) {
                return null;
              }

              if (step.__typename === "AutopilotSearchDesignTextOnlyStep") {
                return (
                  <CuratedSearchDesignTextOnlyStep
                    key={idx}
                    isCurrentStep={
                      idx === processedSteps.length - 1 && isRunning
                    }
                    data={getFragmentData(
                      CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                      makeFragmentData(
                        step,
                        CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                      ),
                    )}
                  />
                );
              }
              if (step.__typename === "AutopilotSessionPreviewContactStep") {
                return (
                  <div key={idx} className={classNames("mt-1")}>
                    <AutopilotPreviewContactStep
                      contact={getFragmentData(
                        AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
                        makeFragmentData(
                          step.autopilotSessionPreviewContactStepData,
                          AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
                        ),
                      )}
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
        {previewStep && (
          <AutopilotPreview
            setMinimized={setMinimized}
            autopilotSessionPreview={previewStep}
            curatedSearchId={curatedSearchId}
            isRemixStep={isRemixStep}
            previewContactFeedback={previewContactFeedback}
            setPreviewContactFeedback={setPreviewContactFeedback}
            existingAutopilotSession={
              getFragmentData(
                AutopilotPreview_AutopilotSessionFragmentDoc,
                existingAutopilotSession,
              ) ?? null
            }
          />
        )}
      </CiroExpandableCard>
    </div>
  );
};

export default AutopilotPreviewSteps;
